import { Home, Refresh } from "@mui/icons-material";
import { Box, Breadcrumbs, Button, CircularProgress, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { COLORS } from "assets";
import { BREAK_GLASS_APPROVED_EVENT, ENDPOINTS, MSG_CODE } from "config/constants";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import PrivateLayout from "layouts/PrivateLayout";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ThreatModel } from "types";
import { IncidentAlertLink } from "pages/incidents/alertLink";
import ThreatActions from "./actions";
import ThreatDetails from "./details";

interface Data {
  threat: ThreatModel;
}

export default function Threat() {
  const [reload, setReload] = useState(false);
  const t = useTranslation();
  const {
    isLoading: isLoadingGet,
    response: responseGet,
    error: errorGet,
    callFetch: callFetchGet
  } = useFetch<Data>("GET");

  const threat = useMemo(() => {
    return responseGet?.data?.threat ?? null;
  }, [responseGet]);

  const { enqueueSnackbar } = useSnackbar();

  const onReload = useCallback(() => setReload((prevState) => !prevState), []);
  useEffect(() => {
    const onApproved = () => onReload();
    window.addEventListener(BREAK_GLASS_APPROVED_EVENT, onApproved);
    return () => window.removeEventListener(BREAK_GLASS_APPROVED_EVENT, onApproved);
  }, [onReload]);
  useEffect(() => {
    if (errorGet) {
      enqueueSnackbar(errorGet.message || JSON.stringify(errorGet) || "", {
        variant: "error"
      });
    }
  }, [errorGet, enqueueSnackbar]);
  useEffect(() => {
    const split = window.location.pathname.split("/")[2];
    if (split) {
      callFetchGet(ENDPOINTS.threats + `/${split}`).catch(console.error);
    }
  }, [callFetchGet, reload]);

  return (
    <PrivateLayout canAccess={() => true}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1} flexWrap="wrap">
        <Breadcrumbs aria-label="breadcrumb">
          <Tooltip title={t["Home"]}>
            <IconButton LinkComponent={Link} {...{ to: "/" }} size="small" sx={{ color: COLORS.surface.secondaryText }}>
              <Home fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            LinkComponent={Link}
            {...{ to: "/threats" }}
            sx={{ color: COLORS.surface.secondaryText, textTransform: "none" }}
          >
            {t["Threats"]}
          </Button>
          {errorGet ? (
            <Typography color="error.main">
              {errorGet.msgCode === MSG_CODE.BREAK_GLASS_REQUIRED
                ? t["Break-glass required"] || "Break-glass required for this home"
                : t["Invalid Threat ID"]}
            </Typography>
          ) : (
            <Typography color="text.primary">{threat?.threatId}</Typography>
          )}
        </Breadcrumbs>
        <Stack direction="row" alignItems="center" spacing={1}>
          {threat ? <IncidentAlertLink threat={threat} variant="button" /> : null}
          {threat ? <ThreatActions threat={threat} onSuccess={onReload} /> : null}
          {isLoadingGet ? (
            <IconButton disabled>
              <CircularProgress size={22} sx={{ color: COLORS.primary }} />
            </IconButton>
          ) : (
            <Tooltip title="Refresh">
              <IconButton
                onClick={onReload}
                sx={{
                  color: COLORS.surface.primaryText,
                  border: `1px solid ${COLORS.surface.border}`,
                  bgcolor: COLORS.surface.card,
                  borderRadius: `${COLORS.radius.sm}px`
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>
      <ThreatDetails threat={threat} loading={isLoadingGet} />
    </PrivateLayout>
  );
}
